import { useState, useCallback } from "react";
import { useSearchParams } from "react-router";
import {
  Button,
  Heading,
  Row,
  Text,
  Badge,
  Modal,
  Avatar,
  useToast,
} from "@/components/ds";
import { InviteForm } from "./InviteForm";
import type { TeamMember, Invite } from "@/domain/team/types";
import styles from "./TeamList.module.css";

interface TeamListProps {
  readonly initialMembers: readonly TeamMember[];
  readonly initialInvites: readonly Invite[];
}

function isOpenLink(invite: Invite): boolean {
  return invite.email === null;
}

function roleLabel(invite: Invite): string {
  if (invite.isOwner && invite.isCleaner) return "Manager and cleaner";
  if (invite.isOwner) return "Manager";
  return "Cleaner";
}

function SectionChevron({ collapsed }: { readonly collapsed: boolean }) {
  return (
    <svg
      className={`${styles.sectionChevron} ${collapsed ? styles.sectionChevronCollapsed : ""}`}
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M4.22 6.22a.75.75 0 011.06 0L8 8.94l2.72-2.72a.75.75 0 111.06 1.06l-3.25 3.25a.75.75 0 01-1.06 0L4.22 7.28a.75.75 0 010-1.06z"
        clipRule="evenodd"
      />
    </svg>
  );
}

export function TeamList({ initialMembers, initialInvites }: TeamListProps) {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [members, setMembers] = useState<readonly TeamMember[]>(initialMembers);
  const [invites, setInvites] = useState<readonly Invite[]>(initialInvites);
  const [showInviteForm, setShowInviteForm] = useState(
    () => searchParams.get("invite") === "1",
  );
  const [deactivateTarget, setDeactivateTarget] = useState<TeamMember | null>(null);
  const [deactivating, setDeactivating] = useState(false);
  const [showDeactivated, setShowDeactivated] = useState(false);

  const activeMembers = members.filter((m) => m.status === "active");
  const deactivatedMembers = members.filter((m) => m.status === "deactivated");
  const openLinks = invites.filter(isOpenLink);
  const personalInvites = invites.filter((inv) => !isOpenLink(inv));

  const handleInviteSent = useCallback((invite: Invite) => {
    setInvites((prev) => [invite, ...prev]);
  }, []);

  function inviteLinkUrl(token: string): string {
    return `${window.location.origin}/invite/${token}`;
  }

  const handleCopyLink = useCallback(async (token: string) => {
    try {
      await navigator.clipboard.writeText(inviteLinkUrl(token));
      toast("success", "Link copied");
    } catch {
      toast("error", "Couldn't copy link");
    }
  }, [toast]);

  const handleCancelInvite = useCallback(async (inviteId: string) => {
    try {
      const response = await fetch(`/api/team/invites/${inviteId}`, { method: "DELETE" });
      const data = await response.json();
      if (!data.success) {
        toast("error", data.error?.message ?? "Something went wrong. Please try again.");
        return;
      }
      setInvites((prev) => prev.filter((inv) => inv.id !== inviteId));
      toast("success", "Invite cancelled");
    } catch {
      toast("error", "Something went wrong. Please try again.");
    }
  }, [toast]);

  const handleDeactivateLink = useCallback(async (inviteId: string) => {
    try {
      const response = await fetch(`/api/team/invites/${inviteId}`, { method: "DELETE" });
      const data = await response.json();
      if (!data.success) {
        toast("error", data.error?.message ?? "Something went wrong. Please try again.");
        return;
      }
      setInvites((prev) => prev.filter((inv) => inv.id !== inviteId));
      toast("success", "Link deactivated");
    } catch {
      toast("error", "Something went wrong. Please try again.");
    }
  }, [toast]);

  const handleRoleChange = useCallback(async (member: TeamMember, isOwner: boolean, isCleaner: boolean) => {
    if (!isOwner && !isCleaner) {
      toast("error", "At least one role is required");
      return;
    }

    try {
      const response = await fetch(`/api/team/${member.membershipId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isOwner, isCleaner }),
      });

      const data = await response.json();
      if (!data.success) {
        toast("error", data.error?.message ?? "Something went wrong. Please try again.");
        return;
      }

      setMembers((prev) =>
        prev.map((m) =>
          m.membershipId === member.membershipId ? { ...m, isOwner, isCleaner } : m,
        ),
      );
      toast("success", "Role updated");
    } catch {
      toast("error", "Something went wrong. Please try again.");
    }
  }, [toast]);

  const handleDeactivate = useCallback(async () => {
    if (!deactivateTarget) return;

    setDeactivating(true);
    try {
      const response = await fetch(`/api/team/${deactivateTarget.membershipId}`, { method: "DELETE" });
      const data = await response.json();
      if (!data.success) {
        toast("error", data.error?.message ?? "Something went wrong. Please try again.");
        return;
      }

      setMembers((prev) =>
        prev.map((m) =>
          m.membershipId === deactivateTarget.membershipId ? { ...m, status: "deactivated" as const } : m,
        ),
      );
      setDeactivateTarget(null);
      toast("success", `${deactivateTarget.name} has been deactivated`);
    } catch {
      toast("error", "Something went wrong. Please try again.");
    } finally {
      setDeactivating(false);
    }
  }, [deactivateTarget, toast]);

  const handleReactivate = useCallback(async (member: TeamMember) => {
    try {
      const response = await fetch(`/api/team/${member.membershipId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "active" }),
      });

      const data = await response.json();
      if (!data.success) {
        toast("error", data.error?.message ?? "Something went wrong. Please try again.");
        return;
      }

      setMembers((prev) =>
        prev.map((m) =>
          m.membershipId === member.membershipId ? { ...m, status: "active" as const } : m,
        ),
      );
      toast("success", `${member.name} has been reactivated`);
    } catch {
      toast("error", "Something went wrong. Please try again.");
    }
  }, [toast]);

  const hasCardContent = activeMembers.length > 0 || invites.length > 0 || deactivatedMembers.length > 0;

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div className={styles.pageTitle}>
          <Heading level={1} size="h3">Team</Heading>
        </div>
        <Button size="sm" onClick={() => setShowInviteForm(!showInviteForm)}>
          {showInviteForm ? "Close" : "Invite team member"}
        </Button>
      </div>

      {showInviteForm && (
        <div className={styles.inviteSection}>
          <InviteForm onInviteSent={handleInviteSent} />
        </div>
      )}

      {hasCardContent && (
        <div className={styles.tableCard}>
          {openLinks.length > 0 && (
            <>
              <div className={styles.sectionRow}>
                Invite links ({openLinks.length})
              </div>
              {openLinks.map((invite) => (
                <div key={invite.id} className={styles.memberRow}>
                  <div className={styles.memberInfo}>
                    <div className={styles.badges}>
                      {invite.isOwner && <Badge variant="info">Manager</Badge>}
                      {invite.isCleaner && <Badge variant="success">Cleaner</Badge>}
                    </div>
                    <span className={styles.inviteDescription}>{roleLabel(invite)} invite link</span>
                  </div>
                  <div className={styles.inviteLinkGroup}>
                    <input
                      type="text"
                      readOnly
                      value={inviteLinkUrl(invite.token)}
                      className={styles.inviteLinkInput}
                      aria-label="Invite link"
                      onFocus={(e) => e.target.select()}
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleCopyLink(invite.token)}
                    >
                      Copy link
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeactivateLink(invite.id)}
                    >
                      Deactivate link
                    </Button>
                  </div>
                </div>
              ))}
            </>
          )}

          {personalInvites.length > 0 && (
            <>
              <div className={styles.sectionRow}>
                Pending invites ({personalInvites.length})
              </div>
              {personalInvites.map((invite) => (
                <div key={invite.id} className={styles.memberRow}>
                  <div className={styles.memberInfo}>
                    <Avatar name={invite.name ?? ""} size="sm" />
                    <div className={styles.memberDetails}>
                      <span className={styles.memberName}>{invite.name}</span>
                      <span className={styles.memberEmail}>{invite.email}</span>
                    </div>
                    <div className={styles.badges}>
                      <Badge variant="warning">Pending</Badge>
                      {invite.isOwner && <Badge variant="info">Manager</Badge>}
                      {invite.isCleaner && <Badge variant="success">Cleaner</Badge>}
                    </div>
                  </div>
                  <div className={styles.inviteLinkGroup}>
                    <input
                      type="text"
                      readOnly
                      value={inviteLinkUrl(invite.token)}
                      className={styles.inviteLinkInput}
                      aria-label="Invite link"
                      onFocus={(e) => e.target.select()}
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleCopyLink(invite.token)}
                    >
                      Copy link
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCancelInvite(invite.id)}
                    >
                      Cancel invite
                    </Button>
                  </div>
                </div>
              ))}
            </>
          )}

          {activeMembers.length > 0 && (
            <>
              <div className={styles.sectionRow}>
                Active members ({activeMembers.length})
              </div>
              {activeMembers.map((member) => (
                <div key={member.membershipId} className={styles.memberRow}>
                  <div className={styles.memberInfo}>
                    <Avatar name={member.name} size="sm" />
                    <div className={styles.memberDetails}>
                      <span className={styles.memberName}>
                        {member.name}
                        {member.isAccountCreator && (
                          <span className={styles.creatorTag}>Account creator</span>
                        )}
                      </span>
                      <span className={styles.memberEmail}>{member.email}</span>
                    </div>
                    <div className={styles.badges}>
                      {member.isOwner && <Badge variant="info">Manager</Badge>}
                      {member.isCleaner && <Badge variant="success">Cleaner</Badge>}
                    </div>
                  </div>
                  <div className={styles.memberActions}>
                    {member.isOwner
                      ? member.isCleaner && !member.isAccountCreator && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRoleChange(member, false, member.isCleaner)}
                          >
                            Remove manager
                          </Button>
                        )
                      : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRoleChange(member, true, member.isCleaner)}
                          >
                            Make manager
                          </Button>
                        )}
                    {member.isCleaner
                      ? member.isOwner && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRoleChange(member, member.isOwner, false)}
                          >
                            Remove cleaner
                          </Button>
                        )
                      : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRoleChange(member, member.isOwner, true)}
                          >
                            Make cleaner
                          </Button>
                        )}
                    {!member.isAccountCreator && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeactivateTarget(member)}
                      >
                        Deactivate
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}

          {deactivatedMembers.length > 0 && (
            <>
              <button
                type="button"
                className={styles.sectionRowButton}
                onClick={() => setShowDeactivated(!showDeactivated)}
                aria-expanded={showDeactivated}
              >
                <span>Deactivated ({deactivatedMembers.length})</span>
                <SectionChevron collapsed={!showDeactivated} />
              </button>

              {showDeactivated && deactivatedMembers.map((member) => (
                <div key={member.membershipId} className={`${styles.memberRow} ${styles.deactivated}`}>
                  <div className={styles.memberInfo}>
                    <Avatar name={member.name} size="sm" />
                    <div className={styles.memberDetails}>
                      <span className={styles.memberName}>{member.name}</span>
                      <span className={styles.memberEmail}>{member.email}</span>
                    </div>
                  </div>
                  <div className={styles.memberActions}>
                    <Badge variant="neutral">Deactivated</Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleReactivate(member)}
                    >
                      Reactivate
                    </Button>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      <Modal
        open={deactivateTarget !== null}
        onClose={() => setDeactivateTarget(null)}
        title="Deactivate team member"
        size="sm"
        footer={
          <Row justify="end" gap={3}>
            <Button variant="secondary" size="sm" onClick={() => setDeactivateTarget(null)}>
              Cancel
            </Button>
            <Button variant="destructive" size="sm" onClick={handleDeactivate} disabled={deactivating}>
              {deactivating ? "Deactivating..." : "Yes, deactivate"}
            </Button>
          </Row>
        }
      >
        <Text>
          Are you sure you want to deactivate {deactivateTarget?.name}?
        </Text>
        <Text size="sm" color="muted">
          They won&apos;t be able to log in or be scheduled. You can reactivate them later.
        </Text>
      </Modal>
    </div>
  );
}
